if (!customElements.get('wide-lazy-video')) {
  class WideLazyVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.previewDuration = Number(this.dataset.previewDuration || 3);
      this.mediaHost = this.querySelector('[data-wlv-media]');
      this.template = this.querySelector('template[data-wlv-template]');
      this.expandButton = this.querySelector('[data-wlv-expand]');
      this.collapseButton = this.querySelector('[data-wlv-collapse]');

      this.handleExpand = () => this.expand();
      this.handleCollapse = () => this.collapse(true);
      this.handleVisibilityChange = () => {
        if (document.hidden) {
          this.pause();
        } else if (this.isVisible && !this.hasAttribute('expanded')) {
          this.startPreview();
        }
      };
      this.handleKeydown = (event) => {
        if (event.key === 'Escape' && this.hasAttribute('expanded')) this.collapse(true);
      };

      this.expandButton?.addEventListener('click', this.handleExpand);
      this.collapseButton?.addEventListener('click', this.handleCollapse);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      document.addEventListener('keydown', this.handleKeydown);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => this.handleIntersection(entry), {
          rootMargin: '250px 0px',
          threshold: 0.05,
        });
        this.observer.observe(this);
      } else {
        this.isVisible = true;
        this.hydrate();
        this.startPreview();
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.pauseTimer);
      window.clearTimeout(this.unloadTimer);
      this.pause();
      this.observer?.disconnect();
      this.expandButton?.removeEventListener('click', this.handleExpand);
      this.collapseButton?.removeEventListener('click', this.handleCollapse);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.removeEventListener('keydown', this.handleKeydown);
      this.initialized = false;
    }

    handleIntersection(entry) {
      this.isVisible = entry.isIntersecting;
      window.clearTimeout(this.pauseTimer);
      window.clearTimeout(this.unloadTimer);

      if (this.isVisible) {
        this.hydrate();
        if (!this.hasAttribute('expanded')) this.startPreview();
        return;
      }

      this.pauseTimer = window.setTimeout(() => this.pause(), 1000);
      if (!this.hasAttribute('expanded')) {
        this.unloadTimer = window.setTimeout(() => this.unload(), 12000);
      }
    }

    hydrate() {
      if (this.video || !this.template || !this.mediaHost) return;

      this.mediaHost.appendChild(this.template.content.cloneNode(true));
      this.video = this.mediaHost.querySelector('video');
      if (!this.video) return;

      this.handleTimeUpdate = () => {
        if (!this.previewMode) return;
        const limit = Math.min(this.previewDuration, Number.isFinite(this.video.duration) ? this.video.duration : this.previewDuration);
        if (this.video.currentTime >= limit) {
          this.video.currentTime = 0;
          this.video.play().catch(() => {});
        }
      };
      this.handlePlaying = () => this.setAttribute('media-playing', '');
      this.handleEnded = () => {
        if (!this.previewMode) return;
        this.video.currentTime = 0;
        this.video.play().catch(() => {});
      };
      this.video.addEventListener('timeupdate', this.handleTimeUpdate);
      this.video.addEventListener('playing', this.handlePlaying);
      this.video.addEventListener('ended', this.handleEnded);
      this.video.load();
    }

    startPreview() {
      this.hydrate();
      if (!this.video || this.hasAttribute('expanded') || document.hidden) return;

      this.previewMode = true;
      this.video.controls = false;
      this.video.muted = true;
      this.video.playsInline = true;

      if (this.video.currentTime >= this.previewDuration) this.video.currentTime = 0;
      this.video.play().catch(() => {});
    }

    expand() {
      this.hydrate();
      if (!this.video) return;

      window.clearTimeout(this.pauseTimer);
      window.clearTimeout(this.unloadTimer);
      this.previewMode = false;
      this.setAttribute('expanded', '');
      this.expandButton?.setAttribute('aria-expanded', 'true');
      this.video.controls = true;
      this.video.muted = false;
      this.video.currentTime = 0;
      this.video.play().catch(() => {
        this.video.muted = true;
        this.video.play().catch(() => {});
      });
      requestAnimationFrame(() => this.collapseButton?.focus({ preventScroll: true }));
    }

    collapse(restoreFocus = false) {
      if (!this.hasAttribute('expanded')) return;

      this.removeAttribute('expanded');
      this.expandButton?.setAttribute('aria-expanded', 'false');
      if (this.video) {
        this.video.controls = false;
        this.video.muted = true;
        this.video.currentTime = 0;
      }

      if (this.isVisible) this.startPreview();
      else this.pause();
      if (restoreFocus) this.expandButton?.focus({ preventScroll: true });
    }

    pause() {
      this.video?.pause();
    }

    unload() {
      if (!this.video || this.hasAttribute('expanded')) return;

      this.video.pause();
      this.video.removeEventListener('timeupdate', this.handleTimeUpdate);
      this.video.removeEventListener('playing', this.handlePlaying);
      this.video.removeEventListener('ended', this.handleEnded);
      this.mediaHost.replaceChildren();
      this.video = null;
      this.previewMode = false;
      this.removeAttribute('media-playing');
    }
  }

  customElements.define('wide-lazy-video', WideLazyVideo);
}
